import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { api, formatMAD, resolveMediaUrl } from "@/lib/api";
import { buildOrderWhatsAppUrl } from "@/lib/whatsapp";
import { Truck, ShieldCheck, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const MOROCCAN_CITIES = [
    "Casablanca",
    "Rabat",
    "Marrakech",
    "Fès",
    "Tanger",
    "Agadir",
    "Meknès",
    "Oujda",
    "Kénitra",
    "Tétouan",
    "Salé",
    "Nador",
    "El Jadida",
    "Béni Mellal",
];

export default function Checkout() {
    const navigate = useNavigate();
    const { items, subtotal, clear } = useCart();
    const { t } = useI18n();
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        address: "",
        city: "",
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [promoInput, setPromoInput] = useState("");
    const [promo, setPromo] = useState(null); // {code, percent}
    const [promoLoading, setPromoLoading] = useState(false);
    const [promoError, setPromoError] = useState("");

    const shipping = items.length > 0 ? 30 : 0;
    const discount = promo ? Math.floor((subtotal * promo.percent) / 100) : 0;
    const total = subtotal - discount + shipping;

    const applyPromo = async () => {
        const code = promoInput.trim();
        if (!code) return;
        setPromoLoading(true);
        setPromoError("");
        try {
            const res = await api.get(`/promo/${encodeURIComponent(code)}`);
            if (res.data.valid) {
                setPromo({ code: res.data.code, percent: res.data.percent });
                setPromoInput(res.data.code);
                toast.success(t("checkout.promo_applied"));
            } else {
                setPromo(null);
                setPromoError(t("checkout.promo_invalid"));
            }
        } catch (_) {
            setPromoError(t("checkout.promo_invalid"));
        } finally {
            setPromoLoading(false);
        }
    };

    const removePromo = () => {
        setPromo(null);
        setPromoInput("");
        setPromoError("");
    };

    if (items.length === 0) {
        return (
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-32 text-center">
                <p className="font-display text-3xl uppercase font-black">
                    {t("checkout.empty_title")}
                </p>
                <p className="text-white/50 mt-3">{t("checkout.empty_desc")}</p>
                <Link
                    to="/collection"
                    className="mt-8 inline-flex items-center justify-center px-8 h-12 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold"
                    data-testid="checkout-empty-shop-btn"
                >
                    {t("checkout.see_collection")}
                </Link>
            </div>
        );
    }

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.address || !form.city) {
            toast.error(t("checkout.err_fields"));
            return;
        }
        if (!/^[0-9+\s-]{8,}$/.test(form.phone)) {
            toast.error(t("checkout.err_phone"));
            return;
        }
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error(t("checkout.err_email"));
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                items: items.map((i) => ({
                    product_id: i.product_id,
                    name: i.name,
                    price: i.price,
                    size: i.size,
                    quantity: i.quantity,
                    image: i.image,
                })),
                customer: {
                    name: form.name,
                    phone: form.phone,
                    email: form.email.trim() || null,
                    address: form.address,
                    city: form.city,
                    notes: form.notes || null,
                },
                promo_code: promo?.code || null,
            };
            const res = await api.post("/orders", payload);
            clear();
            // Open WhatsApp with pre-filled order summary (new tab, no-op if blocked)
            try {
                const waUrl = buildOrderWhatsAppUrl(res.data, formatMAD);
                window.open(waUrl, "_blank", "noopener,noreferrer");
            } catch (_) {}
            navigate(`/order/${res.data.order_number}`);
        } catch (err) {
            console.error(err);
            toast.error(t("checkout.err_generic"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="checkout-page" className="mx-auto max-w-[1400px] px-5 sm:px-8 py-8 sm:py-12">
            <Link
                to="/collection"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white mb-8 link-underline"
            >
                <ArrowLeft size={14} className="rtl:rotate-180" /> {t("checkout.continue_shop")}
            </Link>

            <h1 className="font-display text-4xl sm:text-6xl font-black uppercase tracking-tighter mb-10">
                {t("checkout.title")}
            </h1>

            <div className="grid lg:grid-cols-[1fr_420px] gap-10 lg:gap-16 items-start">
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="checkout-form">
                    <section>
                        <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6">
                            {t("checkout.section_contact")}
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field
                                label={t("checkout.name")}
                                required
                                value={form.name}
                                onChange={set("name")}
                                testid="input-name"
                                placeholder={t("checkout.name_placeholder")}
                            />
                            <Field
                                label={t("checkout.phone")}
                                required
                                value={form.phone}
                                onChange={set("phone")}
                                testid="input-phone"
                                type="tel"
                                placeholder={t("checkout.phone_placeholder")}
                            />
                        </div>
                    </section>

                    <section>
                        <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6">
                            {t("checkout.section_shipping")}
                        </p>
                        <div className="space-y-4">
                            <Field
                                label={t("checkout.address")}
                                required
                                value={form.address}
                                onChange={set("address")}
                                testid="input-address"
                                placeholder={t("checkout.address_placeholder")}
                            />
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold block mb-2">
                                    {t("checkout.city")} *
                                </label>
                                <select
                                    value={form.city}
                                    onChange={set("city")}
                                    required
                                    className="w-full h-12 bg-transparent border border-white/20 focus:border-white text-white text-sm px-4 outline-none transition-colors"
                                    data-testid="input-city"
                                >
                                    <option value="" className="bg-black">
                                        {t("checkout.city_placeholder")}
                                    </option>
                                    {MOROCCAN_CITIES.map((c) => (
                                        <option key={c} value={c} className="bg-black">
                                            {c}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold block mb-2">
                                    {t("checkout.notes")}
                                </label>
                                <textarea
                                    value={form.notes}
                                    onChange={set("notes")}
                                    rows={3}
                                    placeholder={t("checkout.notes_placeholder")}
                                    className="w-full bg-transparent border border-white/20 focus:border-white text-white text-sm px-4 py-3 outline-none resize-none transition-colors"
                                    data-testid="input-notes"
                                />
                            </div>
                        </div>
                    </section>

                    <section>
                        <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6">
                            {t("checkout.section_payment")}
                        </p>
                        <div className="border border-white p-5 flex items-start gap-4" data-testid="payment-cod">
                            <div className="mt-1 h-5 w-5 border border-white flex items-center justify-center flex-shrink-0">
                                <CheckCircle2 size={12} className="text-white" />
                            </div>
                            <div>
                                <p className="font-display text-base font-bold uppercase tracking-tight">
                                    {t("checkout.cod_title")}
                                </p>
                                <p className="text-sm text-white/60 mt-1">{t("checkout.cod_desc")}</p>
                            </div>
                        </div>
                    </section>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full h-14 bg-white text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-white/90 active:scale-[0.98] transition-all disabled:opacity-60"
                        data-testid="confirm-order-btn"
                    >
                        {submitting
                            ? t("checkout.submitting")
                            : `${t("checkout.confirm")} · ${formatMAD(total)}`}
                    </button>
                </form>

                <aside className="border border-white/10 p-6 lg:sticky lg:top-24" data-testid="checkout-summary">
                    <p className="font-display text-xl font-bold uppercase tracking-tight mb-5">
                        {t("checkout.summary")}
                    </p>
                    <div className="space-y-4 mb-6">
                        {items.map((item) => (
                            <div
                                key={item.key}
                                className="flex gap-3 pb-4 border-b border-white/10 last:border-0 last:pb-0"
                            >
                                <div className="w-16 h-20 bg-white/5 flex-shrink-0 overflow-hidden">
                                    {item.image && (
                                        <img
                                            src={resolveMediaUrl(item.image)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0 text-sm">
                                    <p className="font-semibold uppercase truncate">{item.name}</p>
                                    <p className="text-white/50 text-xs mt-1">
                                        {item.size ? `${t("checkout.size")} ${item.size} · ` : ""}
                                        {t("checkout.qty")} {item.quantity}
                                    </p>
                                </div>
                                <p className="text-sm font-semibold whitespace-nowrap">
                                    {formatMAD(item.price * item.quantity)}
                                </p>
                            </div>
                        ))}
                    </div>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-white/70">
                            <span>{t("cart.subtotal")}</span>
                            <span data-testid="summary-subtotal">{formatMAD(subtotal)}</span>
                        </div>
                        {promo && (
                            <div className="flex justify-between text-white" data-testid="summary-discount-row">
                                <span>
                                    {t("checkout.discount")} · {promo.code} (−{promo.percent}%)
                                </span>
                                <span data-testid="summary-discount">−{formatMAD(discount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-white/70">
                            <span>{t("cart.shipping")}</span>
                            <span>{formatMAD(shipping)}</span>
                        </div>
                        <div className="flex justify-between font-display uppercase font-bold text-lg pt-3 border-t border-white/10 mt-3">
                            <span>{t("cart.total")}</span>
                            <span data-testid="summary-total">{formatMAD(total)}</span>
                        </div>
                    </div>

                    {/* Promo code input */}
                    <div className="mt-6 pt-6 border-t border-white/10">
                        <label className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold block mb-2">
                            {t("checkout.promo_label")}
                        </label>
                        {promo ? (
                            <div
                                className="flex items-center justify-between border border-white p-3"
                                data-testid="promo-applied-row"
                            >
                                <div>
                                    <p className="text-xs uppercase tracking-[0.2em] text-white/50">
                                        {t("checkout.promo_applied")}
                                    </p>
                                    <p className="font-display font-bold text-sm tracking-tight mt-0.5">
                                        {promo.code} · −{promo.percent}%
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={removePromo}
                                    className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white link-underline"
                                    data-testid="promo-remove-btn"
                                >
                                    {t("checkout.promo_remove")}
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={promoInput}
                                        onChange={(e) => {
                                            setPromoInput(e.target.value.toUpperCase());
                                            setPromoError("");
                                        }}
                                        placeholder={t("checkout.promo_placeholder")}
                                        className="flex-1 h-11 bg-transparent border border-white/20 focus:border-white text-white text-sm px-3 outline-none uppercase tracking-widest"
                                        data-testid="promo-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={applyPromo}
                                        disabled={promoLoading || !promoInput.trim()}
                                        className="h-11 px-4 border border-white/30 hover:bg-white hover:text-black text-xs uppercase tracking-[0.2em] font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid="promo-apply-btn"
                                    >
                                        {t("checkout.promo_apply")}
                                    </button>
                                </div>
                                {promoError && (
                                    <p
                                        className="mt-2 text-xs uppercase tracking-[0.2em] text-red-400"
                                        data-testid="promo-error"
                                    >
                                        {promoError}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                    <div className="mt-6 pt-6 border-t border-white/10 space-y-3 text-xs text-white/60">
                        <div className="flex items-center gap-2">
                            <Truck size={14} strokeWidth={1.5} /> {t("product.delivery")}
                        </div>
                        <div className="flex items-center gap-2">
                            <ShieldCheck size={14} strokeWidth={1.5} /> {t("checkout.cod_title")}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function Field({ label, testid, required, ...props }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold block mb-2">
                {label}
                {required && " *"}
            </label>
            <input
                {...props}
                required={required}
                className="w-full h-12 bg-transparent border border-white/20 focus:border-white text-white text-sm px-4 outline-none transition-colors"
                data-testid={testid}
            />
        </div>
    );
}

